(() => {
  const SELECTOR = '[data-sw-install-gallery]';
  const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

  const galleryObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hydrateGallery(entry.target);
          galleryObserver.unobserve(entry.target);
        });
      }, { rootMargin: '300px 0px' })
    : null;

  function parseVideo(rawUrl) {
    try {
      const url = new URL(rawUrl);
      const host = url.hostname.replace(/^www\./, '').toLowerCase();
      const pathParts = url.pathname.split('/').filter(Boolean);
      let provider = '';
      let id = '';
      let isShort = false;

      if (host === 'youtu.be') {
        provider = 'youtube';
        id = pathParts[0] || '';
      } else if (host.endsWith('youtube.com')) {
        provider = 'youtube';
        if (pathParts[0] === 'shorts') {
          id = pathParts[1] || '';
          isShort = true;
        } else if (pathParts[0] === 'embed') {
          id = pathParts[1] || '';
        } else {
          id = url.searchParams.get('v') || '';
        }
      } else if (host.endsWith('vimeo.com')) {
        provider = 'vimeo';
        id = [...pathParts].reverse().find((part) => /^\d+$/.test(part)) || '';
      }

      return id ? { provider, id, isShort } : null;
    } catch (error) {
      return null;
    }
  }

  function createPlayer(video, title) {
    const iframe = document.createElement('iframe');
    iframe.title = title || 'Installation video';
    iframe.loading = 'lazy';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allowFullscreen = true;

    if (video.provider === 'youtube') {
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(video.id)}?rel=0&autoplay=1`;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.className = `sw-install-guide__video-embed${video.isShort ? ' sw-install-guide__video-embed--short' : ''}`;
    } else {
      iframe.src = `https://player.vimeo.com/video/${encodeURIComponent(video.id)}?autoplay=1`;
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    }

    return iframe;
  }

  function enableImageFallback(image) {
    if (!image) return;

    const useFallback = () => {
      const fallbackSrc = image.dataset.fallbackSrc;
      if (!fallbackSrc) return;

      delete image.dataset.fallbackSrc;
      image.src = fallbackSrc;
    };

    image.addEventListener('error', useFallback, { once: true });
    if (image.complete && image.naturalWidth === 0) useFallback();
  }

  function hydrateImage(image) {
    if (!image || image.dataset.swInstallImageHydrated === 'true') return;

    image.dataset.swInstallImageHydrated = 'true';
    enableImageFallback(image);
    if (image.dataset.src) image.src = image.dataset.src;
  }

  function hydrateGallery(gallery) {
    gallery.querySelectorAll('img[data-src]').forEach(hydrateImage);
  }

  function observeGallery(gallery) {
    if (galleryObserver) {
      galleryObserver.observe(gallery);
    } else {
      hydrateGallery(gallery);
    }
  }

  function mountPlayer(stage, rawUrl, title) {
    const video = parseVideo(rawUrl || '');
    if (!video) return;

    stage.replaceChildren(createPlayer(video, title));
    stage.classList.toggle('sw-install-guide__video--short', video.isShort);
    stage.dataset.videoUrl = rawUrl || '';
    stage.dataset.videoTitle = title || '';
  }

  function addThumbnail(button) {
    const video = parseVideo(button.dataset.videoUrl || '');
    const media = button.querySelector('[data-sw-install-gallery-media]');
    if (!video || !media) return;

    button.classList.toggle('sw-install-gallery__thumb--short', video.isShort);
    media.classList.add(`sw-install-gallery__thumb-media--${video.provider}`);

    if (video.provider === 'youtube') {
      const image = document.createElement('img');
      image.src = TRANSPARENT_PIXEL;
      image.dataset.src = `https://i.ytimg.com/vi/${encodeURIComponent(video.id)}/maxresdefault.jpg`;
      image.dataset.fallbackSrc = `https://i.ytimg.com/vi/${encodeURIComponent(video.id)}/hqdefault.jpg`;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.fetchPriority = 'low';
      media.prepend(image);
    }
  }

  function setupGallery(gallery) {
    if (gallery.dataset.swInstallGalleryReady === 'true') return;
    gallery.dataset.swInstallGalleryReady = 'true';

    const stage = gallery.querySelector('[data-sw-install-gallery-stage]');
    const buttons = [...gallery.querySelectorAll('[data-sw-install-gallery-thumb]')];
    if (!stage) return;

    const initialStageMarkup = stage.innerHTML;

    const setActiveThumbnail = (activeButton = buttons[0]) => {
      buttons.forEach((item) => {
        const active = item === activeButton;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
    };

    const bindPoster = () => {
      const poster = stage.querySelector('[data-sw-install-video-poster]');
      if (!poster) return;

      poster.addEventListener('click', () => {
        mountPlayer(stage, poster.dataset.videoUrl, poster.dataset.videoTitle);
      });
    };

    const stopAndResetPlayer = () => {
      const iframe = stage.querySelector('iframe');
      if (!iframe) return;

      iframe.src = 'about:blank';
      stage.innerHTML = initialStageMarkup;
      stage.classList.remove('sw-install-guide__video--short');
      bindPoster();
      setActiveThumbnail();
      observeGallery(gallery);
    };

    bindPoster();
    gallery.addEventListener('sw-install:stop-video', stopAndResetPlayer);

    buttons.forEach((button) => {
      addThumbnail(button);
      button.addEventListener('click', () => {
        mountPlayer(stage, button.dataset.videoUrl, button.dataset.videoTitle);
        setActiveThumbnail(button);
      });
    });

    observeGallery(gallery);
  }

  function setupAll(root = document) {
    root.querySelectorAll(SELECTOR).forEach(setupGallery);
  }

  setupAll();
  document.addEventListener('sw-install:context-change', (event) => {
    const root = event.target.closest?.('[data-sw-install-guide-navigation]');
    root?.querySelectorAll(SELECTOR).forEach((gallery) => {
      gallery.dispatchEvent(new CustomEvent('sw-install:stop-video'));
    });
  });
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();
